import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useAccount } from './useAccount'
import { API_BASE_URL } from '../constants/system'
import type { LlmLanesResponse } from '../types/llmLanes'

/**
 * The LLM lane census — how deep each model lane is RIGHT NOW, read from the
 * real `llm_request` queue rather than from coding batches.
 *
 * Two faces of one shape, so one hook serves both:
 *
 *   - `{ kind: 'job' }`   GET /autocoder/:customer/:job/lanes — the Activity
 *     tab. The scope is the PATH; nothing in this hook can widen it.
 *   - `{ kind: 'admin' }` GET /admin/llm-lanes — Managers only, customer/job
 *     optional narrowing filters; absent means the whole queue.
 *
 * `enabled` is the mount gate: a tab nobody is looking at must not poll (see
 * PipelineThroughputTab's `active` prop for the same contract), and
 * `refetchIntervalInBackground: false` stops a backgrounded browser tab from
 * hammering the aggregate every ten seconds forever.
 */
export type LlmLanesScope =
  | { kind: 'job'; customer: string; job: string }
  | { kind: 'admin'; customer?: string; job?: string }

/** Default cadence. Depth is a live number; ten seconds is the house rate. */
export const LANES_POLL_SECONDS = 10

export interface UseLlmLanesResult {
  data: LlmLanesResponse | undefined
  loading: boolean
  isFetching: boolean
  error: string | null
  refetch: () => void
}

const fetchLanes = async (scope: LlmLanesScope): Promise<LlmLanesResponse> => {
  if (scope.kind === 'job') {
    const url =
      `${API_BASE_URL}/autocoder/${encodeURIComponent(scope.customer)}` +
      `/${encodeURIComponent(scope.job)}/lanes`
    const res = await axios.get<LlmLanesResponse>(url, {
      headers: { Accept: 'application/json' },
    })
    return res.data
  }

  const params: Record<string, string> = {}
  if (scope.customer) params.customer = scope.customer
  if (scope.job) params.job = scope.job
  const res = await axios.get<LlmLanesResponse>(`${API_BASE_URL}/admin/llm-lanes`, {
    headers: { Accept: 'application/json' },
    params,
  })
  return res.data
}

export const useLlmLanes = (
  scope: LlmLanesScope,
  enabled = true,
  pollSeconds: number = LANES_POLL_SECONDS,
): UseLlmLanesResult => {
  const account = useAccount()

  // House key shape: ['autocoder', customer, job, <resource>, params]. The admin
  // face keys off the SAME tuple with empty scope segments, so an admin looking
  // at everything and a customer looking at one job can never share a cache
  // entry — the two answers are different censuses.
  const customer = scope.customer ?? ''
  const job = scope.job ?? ''
  const queryKey = ['autocoder', customer, job, 'lanes', { scope: scope.kind }]

  // A job census with no job to name is not a request worth making; the admin
  // face has no such requirement (no filters = the whole queue).
  const scoped = scope.kind === 'job' ? !!customer && !!job : true

  const query = useQuery<LlmLanesResponse, Error>({
    queryKey,
    queryFn: () => fetchLanes(scope),
    enabled: enabled && scoped && !!account.user,
    refetchInterval: Math.max(5, pollSeconds) * 1000,
    refetchIntervalInBackground: false,
    staleTime: 5 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error ? query.error.message : null,
    refetch: () => {
      void query.refetch()
    },
  }
}

export default useLlmLanes
